import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Package, Truck } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Button } from '@terra/ui';

import { api } from '../lib/api';

const cellClass =
  'border-border-strong/40 bg-surface text-ink h-9 w-full rounded-lg border px-2.5 text-xs focus:border-gold-400 focus:outline-none';

interface Row {
  id: string;
  label: string;
  sku: string;
  stock: number;
  price: string;
  mrp: string;
  weightGrams: string;
  lengthCm: string;
  widthCm: string;
  heightCm: string;
}

const EDITABLE = ['price', 'mrp', 'weightGrams', 'lengthCm', 'widthCm', 'heightCm'] as const;
type Editable = (typeof EDITABLE)[number];
const HEADINGS: Record<Editable, string> = {
  price: 'Price (₹)',
  mrp: 'MRP (₹)',
  weightGrams: 'Weight (g)',
  lengthCm: 'L (cm)',
  widthCm: 'B (cm)',
  heightCm: 'H (cm)',
};

const toCm = (mm: number | null | undefined) => (mm && mm > 0 ? String(mm / 10) : '');
const toMm = (cm: string) => {
  const n = parseFloat(cm);
  return Number.isFinite(n) && n > 0 ? Math.round(n * 10) : undefined;
};
const toRupees = (paise: number | null | undefined) =>
  paise && paise > 0 ? String(paise / 100) : '';
const toPaise = (rupees: string) => {
  const n = parseFloat(rupees);
  return Number.isFinite(n) && n > 0 ? Math.round(n * 100) : undefined;
};

/**
 * Price, MRP, weight and packed box size of each variant of an existing
 * product, with its current stock for reference (stock is changed on the
 * Inventory page). Orders keep the price they were placed at, so a new price
 * applies to new orders only.
 *
 * The courier is booked on the weight and box size. They could not be edited
 * anywhere before — the form pointed to a "Variants tab" that did not exist —
 * so every parcel shipped on guessed dimensions.
 */
export function VariantShippingEditor({
  variants,
}: {
  variants: Array<{
    id: string;
    sku?: string | null;
    optionValues?: Record<string, string> | null;
    pricePaise?: number | null;
    mrpPaise?: number | null;
    weightGrams?: number | null;
    lengthMm?: number | null;
    widthMm?: number | null;
    heightMm?: number | null;
    inventoryItems?: Array<{ onHand: number; reserved: number }> | null;
  }>;
}) {
  const qc = useQueryClient();
  const [rows, setRows] = useState<Row[]>([]);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    setRows(
      variants.map((v) => ({
        id: v.id,
        label: Object.values(v.optionValues ?? {}).join(' / ') || 'Default',
        sku: v.sku ?? '',
        stock: (v.inventoryItems ?? []).reduce((n, i) => n + i.onHand - i.reserved, 0),
        price: toRupees(v.pricePaise),
        mrp: toRupees(v.mrpPaise),
        weightGrams: v.weightGrams ? String(v.weightGrams) : '',
        lengthCm: toCm(v.lengthMm),
        widthCm: toCm(v.widthMm),
        heightCm: toCm(v.heightMm),
      })),
    );
  }, [variants]);

  const save = useMutation({
    mutationFn: async () => {
      for (const r of rows) {
        const [pricePaise, mrpPaise] = [toPaise(r.price), toPaise(r.mrp)];
        if (!pricePaise || !mrpPaise) {
          throw new Error(`Enter a price and MRP for ${r.label}`);
        }
        if (pricePaise > mrpPaise) {
          throw new Error(`The price of ${r.label} cannot be more than its MRP`);
        }
      }
      for (const r of rows) {
        const weight = parseInt(r.weightGrams, 10);
        const [lengthMm, widthMm, heightMm] = [toMm(r.lengthCm), toMm(r.widthCm), toMm(r.heightCm)];
        const body = {
          pricePaise: toPaise(r.price)!,
          mrpPaise: toPaise(r.mrp)!,
          ...(Number.isFinite(weight) && weight > 0 ? { weightGrams: weight } : {}),
          ...(lengthMm ? { lengthMm } : {}),
          ...(widthMm ? { widthMm } : {}),
          ...(heightMm ? { heightMm } : {}),
        };
        await api.products.updateVariant(r.id, body);
      }
    },
    onSuccess: () => {
      setMessage({
        ok: true,
        text: 'Saved. New orders use the new price; new shipments use the new weight and box size.',
      });
      void qc.invalidateQueries({ queryKey: ['vendor-product-detail'] });
      void qc.invalidateQueries({ queryKey: ['vendor-products'] });
    },
    onError: (e: unknown) =>
      setMessage({
        ok: false,
        text:
          (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
            ?.message ?? (e instanceof Error ? e.message : 'Could not save'),
      }),
  });

  const set = (i: number, k: Editable, v: string) => {
    setMessage(null);
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  };

  const missing = rows.filter((r) => !r.lengthCm || !r.widthCm || !r.heightCm).length;

  return (
    <div className="space-y-3 rounded-xl border border-sky-200 bg-sky-50/40 p-4">
      <div className="flex items-start gap-2.5">
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-sky-100 text-sky-700">
          <Truck className="h-4 w-4" />
        </span>
        <div>
          <p className="text-[14px] font-bold text-slate-900">
            Price, shipping weight &amp; packed box size
          </p>
          <p className="text-[12.5px] text-slate-600">
            Measure the box as packed for the courier. Couriers charge on the larger of actual and
            box-size weight, and bill the difference if a parcel is re-measured. Stock is changed on
            the Inventory page.
          </p>
        </div>
      </div>
      {missing > 0 && (
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-[12.5px] font-semibold text-amber-800">
          {missing} {missing === 1 ? 'variant has' : 'variants have'} no box size — shipments for it
          go out on an estimate.
        </p>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-left text-[12.5px]">
          <thead className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
            <tr>
              <th className="py-2 pr-3">Variant</th>
              <th className="px-1 py-2">SKU</th>
              <th className="w-16 px-1 py-2">Stock</th>
              {EDITABLE.map((k) => (
                <th key={k} className="w-24 px-1 py-2">
                  {HEADINGS[k]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.id} className="border-t border-sky-100">
                <td className="py-2 pr-3 font-semibold text-slate-800">
                  <span className="inline-flex items-center gap-1.5">
                    <Package className="h-3.5 w-3.5 text-slate-400" />
                    {r.label}
                  </span>
                </td>
                <td className="px-1 py-2 font-mono text-[11.5px] text-slate-600">{r.sku || '—'}</td>
                <td className="px-1 py-2 font-semibold text-slate-700">{r.stock}</td>
                {EDITABLE.map((k) => (
                  <td key={k} className="px-1 py-2">
                    <input
                      type="number"
                      min={0}
                      step={k === 'weightGrams' ? 1 : k === 'price' || k === 'mrp' ? 0.01 : 0.1}
                      className={cellClass}
                      value={r[k]}
                      onChange={(e) => set(i, k, e.target.value)}
                      placeholder={k === 'weightGrams' ? '250' : '0'}
                      aria-label={`${HEADINGS[k]} for ${r.label}`}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        {message ? (
          <p
            className={`text-[12.5px] font-semibold ${message.ok ? 'text-emerald-700' : 'text-rose-700'}`}
          >
            {message.text}
          </p>
        ) : (
          <span />
        )}
        <Button type="button" onClick={() => save.mutate()} loading={save.isPending}>
          Save price &amp; shipping
        </Button>
      </div>
    </div>
  );
}
